import { MouseEvent, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import Textarea from "@/components/Textarea";
import { Radio } from "@/components/common/radio";
import { InnerBlockType, IQuestionOptions } from "@/types";
import { ErrorInnerBlock } from "@/components/common/errorInnerBlock";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { Button, ButtonType } from "@/components/common/button";

type SingleChoiceAnswer = {
  answer: string;
  inputValue?: string;
};

const trackEverflowConsentEvent = (label: string) => {
  if (!label?.toUpperCase()?.includes("I CONSENT")) return;
};

const SingleChoiceQuestion = ({
  question,
  questionValue,

  setQuestionAnswer,
  setQuestionValue,
}: QuizQuestionProps<SingleChoiceAnswer>) => {
  const { options } = question.attributes;
  const nodeRef = useRef(null);

  useEffect(() => {
    if (typeof questionValue === "string") {
      setQuestionValue({
        answer: questionValue,
      });
    }
  }, [questionValue]);

  const selectedOption = options?.find(
    (option) => option.value === questionValue.answer
  );
  const hasNoPendingInnerBlocks =
    !selectedOption?.innerBlock || !!questionValue.inputValue?.trim();

  const showSubmitBtn = !!selectedOption?.innerBlock;

  const handleOptionClick = (e: MouseEvent, option: IQuestionOptions) => {
    e.stopPropagation();
    // prevent double clicks
    e.preventDefault();

    trackEverflowConsentEvent(option.label);

    handleChange(option.value);
  };

  const handleChange = (value: string) => {
    const questionValueBySelectedValue =
      questionValue?.answer === value ? questionValue : null;

    const newQuestionValue = {
      ...questionValueBySelectedValue,
      answer: value,
    };

    const canSubmitAnswer = !options?.find((option) => option.value === value)
      ?.innerBlock;

    if (canSubmitAnswer) {
      setQuestionAnswer(newQuestionValue);
    } else {
      setQuestionValue(newQuestionValue);
    }
  };

  const handleTextareaInputChange = (value: string) => {
    setQuestionValue({
      ...(questionValue || {}),
      inputValue: value,
    });
  };

  return (
    <div>
      <div className="my-4 sm:my-6 lg:my-8 flex flex-col space-y-3 sm:space-y-4">
        {options?.map(({ value, label, innerBlock, ...optionRest }) => (
          <label
            key={value}
            htmlFor={value}
            className={classNames(
              "group p-3 sm:p-5 lg:p-6 pr-6 sm:pr-10 lg:pr-11 flex flex-col border border-slate-gray rounded-md cursor-pointer hover:border-dark-purple overflow-hidden",
              {
                "border-dark-purple": questionValue.answer === value,
              }
            )}
            onClick={(e) =>
              handleOptionClick(e, { value, label, innerBlock, ...optionRest })
            }
          >
            <div className="flex">
              <Radio value={value} checked={questionValue.answer === value} />
              <div className="pl-6 sm:pl-7 lg:pl-8 grow">
                <p className="text-lg sm:text-xl text-charcoal font-bold break-words overflow-wrap tracking-[-0.0113em]">
                  {label}
                </p>
                {innerBlock && (
                  <CSSTransition
                    nodeRef={nodeRef}
                    in={innerBlock && questionValue.answer === value}
                    timeout={300}
                    classNames="fade"
                    unmountOnExit
                  >
                    <label
                      ref={nodeRef}
                      htmlFor={innerBlock.label}
                      className="gap-1.5 sm:gap-2.5 mt-4 sm:mt-5 flex flex-col"
                    >
                      {innerBlock.type === InnerBlockType.Error ? (
                        <ErrorInnerBlock
                          label={innerBlock.label}
                          className="text-red"
                        />
                      ) : (
                        <span className="text-sm sm:text-base lg:text-[17px] font-normal">
                          {innerBlock.label}
                        </span>
                      )}
                      {innerBlock?.type === InnerBlockType.Textarea && (
                        <Textarea
                          id={innerBlock.label}
                          placeholder={innerBlock.placeholder}
                          value={questionValue.inputValue}
                          onChange={handleTextareaInputChange}
                        />
                      )}
                    </label>
                  </CSSTransition>
                )}
              </div>
            </div>
          </label>
        ))}
      </div>

      {/* {showSubmitBtn && ( */}
      <Button
        type={ButtonType.NEXT}
        disabled={!questionValue?.answer || !hasNoPendingInnerBlocks}
        onClick={() => setQuestionAnswer(questionValue)}
        showOnlyBackButton={!showSubmitBtn}
      />
      {/* )} */}
    </div>
  );
};

export { SingleChoiceQuestion };
